import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X } from 'lucide-react';
import { eventBus } from '@/map/core/events';
import { useAppStore } from '@/stores/appStore';

/**
 * Scenario playback controls — a circular Play/Pause toggle and an Exit
 * button, bottom-right. Pause freezes the entire scene (vehicles, effects,
 * screens, step progression) via the global pause clock; Play resumes exactly
 * where it left off. No step seeking — playback is continuous.
 */
export function ScenarioControls() {
  const [active, setActive] = useState(false);
  const [paused, setPaused] = useState(false);
  const stopScenario = useAppStore((s) => s.stopScenario);

  useEffect(() => {
    const unsub = eventBus.on('scenario:state', ({ active, paused }) => {
      setActive(active);
      setPaused(paused);
    });
    const unsubEnd = eventBus.on('scenario:end', () => setActive(false));
    return () => { unsub(); unsubEnd(); };
  }, []);

  const togglePause = () => eventBus.emit('timeline:command', { action: 'toggle' });

  const exit = () => {
    eventBus.emit('timeline:command', { action: 'stop' });
    stopScenario();
  };

  return createPortal(
    <AnimatePresence>
      {active && (
        <motion.div
          className="fixed bottom-6 right-6 z-[300] flex items-center gap-3 pointer-events-auto"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.25 }}
        >
          <button
            onClick={togglePause}
            title={paused ? 'Play' : 'Pause'}
            className="w-14 h-14 rounded-full flex items-center justify-center bg-[#00b8a9] text-[#021014] shadow-[0_0_24px_rgba(0,184,169,0.5)] hover:bg-[#15c7b8] hover:scale-105 active:scale-95 transition-all"
          >
            {paused ? (
              <Play size={22} fill="currentColor" className="ml-0.5" />
            ) : (
              <Pause size={22} fill="currentColor" />
            )}
          </button>

          <button
            onClick={exit}
            title="Exit scenario"
            className="w-12 h-12 rounded-full flex items-center justify-center bg-black/60 backdrop-blur-md border border-white/20 text-slate-200 hover:bg-red-500/30 hover:border-red-400/50 hover:text-white hover:scale-105 active:scale-95 transition-all"
          >
            <X size={20} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
